import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, FileText, CheckCircle, AlertTriangle, Calendar, Building, MapPin, Users, Shield, Info, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CFELogo } from "@/components/CFELogo";

export default function CommentRemplirCFE() {
  const navigate = useNavigate();

  // Set document title and meta tags for SEO
  useEffect(() => {
    document.title = "Comment remplir la CFE auto-entrepreneur (formulaire 1447-C-SD)";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Guide clair pour remplir la CFE auto-entrepreneur. Explication pas à pas du formulaire 1447-C-SD + génération automatique du PDF officiel.");
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Guide clair pour remplir la CFE auto-entrepreneur. Explication pas à pas du formulaire 1447-C-SD + génération automatique du PDF officiel.";
      document.head.appendChild(meta);
    }

    // Cleanup on unmount
    return () => {
      document.title = "CFE Auto-Entrepreneur | Déclaration CFE simplifiée - Formulaire 1447-C-SD";
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <a href="/" className="flex items-center gap-2">
              <CFELogo size={28} />
              <span className="font-bold text-lg text-[#0F172A]">CFE Auto-Entrepreneur</span>
            </a>
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
            >
              <a href="/#formulaire" className="flex items-center text-white no-underline">
                Commencer
              </a>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          
          {/* Breadcrumb */}
          <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
            <a href="/" className="hover:text-[#2563EB]">Accueil</a>
            <span className="mx-2">/</span>
            <span className="text-[#0F172A]">Comment remplir la CFE auto-entrepreneur</span>
          </nav>

          {/* H1 - Single main title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6 leading-tight">
            Comment remplir la CFE auto-entrepreneur (formulaire 1447-C-SD)
          </h1>

          {/* Introduction */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <p className="text-lg text-[#475569] leading-relaxed m-0">
              Vous vous demandez <strong>comment remplir la CFE auto-entrepreneur</strong> ? La Cotisation Foncière des Entreprises est un impôt local obligatoire pour tous les auto-entrepreneurs en France. 
              Lors de la création de votre activité, vous devez remplir le <strong>formulaire 1447-C-SD</strong> pour déclarer votre établissement 
              auprès des services fiscaux. Ce guide vous explique comment remplir cette déclaration étape par étape.
            </p>
          </div>

          {/* E-E-A-T Credibility Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <Info className="h-5 w-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#475569]">
              <p className="font-medium text-[#0F172A] mb-1">À propos de ce guide</p>
              <p className="m-0">
                Ce guide est basé sur le <strong>formulaire officiel 1447-C-SD</strong> publié par l'administration fiscale française 
                et les règles en vigueur pour les auto-entrepreneurs (micro-entrepreneurs). 
                Il ne remplace pas un conseil fiscal personnalisé. Ce site n'est pas un service gouvernemental.
              </p>
            </div>
          </div>

          {/* Table of contents */}
          <Card className="mb-10 bg-slate-50 border-slate-200">
            <CardContent className="p-6">
              <p className="font-semibold text-[#0F172A] mb-4">Sommaire</p>
              <ul className="space-y-2 text-[#475569] list-none pl-0 m-0">
                <li><a href="#quest-ce-que-la-cfe" className="hover:text-[#2563EB]">1. Qu'est-ce que la CFE pour un auto-entrepreneur</a></li>
                <li><a href="#quand-remplir" className="hover:text-[#2563EB]">2. Quand faut-il remplir la CFE auto-entrepreneur</a></li>
                <li><a href="#comment-remplir" className="hover:text-[#2563EB]">3. Comment remplir le formulaire 1447-C-SD</a></li>
                <li><a href="#erreurs-frequentes" className="hover:text-[#2563EB]">4. Erreurs fréquentes à éviter</a></li>
                <li><a href="#generer-formulaire" className="hover:text-[#2563EB]">5. Générer automatiquement le formulaire</a></li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 1 */}
          <section id="quest-ce-que-la-cfe" className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
              <Building className="h-6 w-6 text-[#2563EB]" />
              Qu'est-ce que la CFE pour un auto-entrepreneur
            </h2>
            
            <p className="text-[#475569] leading-relaxed mb-4">
              La <strong>Cotisation Foncière des Entreprises (CFE)</strong> est un impôt local qui remplace l'ancienne taxe professionnelle. 
              Elle est due par toutes les personnes physiques ou morales qui exercent une activité professionnelle non salariée, 
              y compris les <strong>auto-entrepreneurs</strong> (micro-entrepreneurs).
            </p>

            <p className="text-[#475569] leading-relaxed mb-4">
              La CFE est calculée sur la base de la <strong>valeur locative des biens immobiliers</strong> utilisés pour l'exercice 
              de votre activité professionnelle. Pour les auto-entrepreneurs travaillant à domicile ou sans local fixe, 
              une <strong>cotisation minimum</strong> est appliquée, dont le montant varie selon les communes.
            </p>

            <div className="bg-blue-50 border-l-4 border-[#2563EB] p-4 my-6">
              <p className="text-[#1e40af] m-0">
                <strong>Bon à savoir :</strong> Les auto-entrepreneurs sont exonérés de CFE la première année civile d'activité. 
                L'exonération peut également s'appliquer si votre chiffre d'affaires est inférieur à 5 000 € par an.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="quand-remplir" className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
              <Calendar className="h-6 w-6 text-[#2563EB]" />
              Quand faut-il remplir la CFE auto-entrepreneur
            </h2>

            <p className="text-[#475569] leading-relaxed mb-4">
              Le formulaire 1447-C-SD doit être rempli et envoyé aux services fiscaux dans les situations suivantes :
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Création d'activité :</strong> Lors du démarrage de votre auto-entreprise, vous devez déposer une déclaration initiale avant le 31 décembre de l'année de création.</span>
              </li>
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Changement de local :</strong> Si vous déménagez votre activité dans une autre commune ou un nouveau local.</span>
              </li>
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Reprise d'établissement :</strong> En cas d'acquisition ou de transfert d'une activité existante.</span>
              </li>
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Modification de surface :</strong> Si la surface utilisée pour votre activité change significativement.</span>
              </li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6">
              <p className="text-amber-800 m-0">
                <strong>Important :</strong> Le formulaire doit être envoyé au <strong>Service des Impôts des Entreprises (SIE)</strong> dont 
                dépend votre établissement, ou déposé via votre espace professionnel sur impots.gouv.fr.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="comment-remplir" className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
              <FileText className="h-6 w-6 text-[#2563EB]" />
              Comment remplir le formulaire 1447-C-SD (pas à pas)
            </h2>

            <p className="text-[#475569] leading-relaxed mb-6">
              Le formulaire 1447-C-SD est structuré en plusieurs sections. Voici comment compléter chaque partie :
            </p>

            {/* A1 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <span className="bg-[#2563EB] text-white text-sm px-2 py-1 rounded">A1</span>
                Identification de l'entreprise
              </h3>
              <p className="text-[#475569] leading-relaxed mb-3">
                Cette section concerne vos informations d'identité et d'immatriculation :
              </p>
              <ul className="space-y-2 text-[#475569] mb-4">
                <li>• <strong>Dénomination :</strong> Votre nom et prénom (ou raison sociale)</li>
                <li>• <strong>Numéro SIRET :</strong> Les 14 chiffres de votre numéro SIRET</li>
                <li>• <strong>Code NACE/APE :</strong> Le code à 4 chiffres et 1 lettre définissant votre activité</li>
                <li>• <strong>Adresse de l'établissement :</strong> L'adresse où vous exercez votre activité</li>
                <li>• <strong>Activité principale :</strong> Description claire de votre activité professionnelle</li>
              </ul>
            </div>

            {/* A2 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <span className="bg-[#2563EB] text-white text-sm px-2 py-1 rounded">A2</span>
                Activité exercée à domicile ou chez les clients
              </h3>
              <p className="text-[#475569] leading-relaxed mb-3">
                Si vous travaillez depuis votre domicile ou directement chez vos clients (sans local dédié), cochez cette case et indiquez :
              </p>
              <ul className="space-y-2 text-[#475569] mb-4">
                <li>• La <strong>surface en m²</strong> dédiée à votre activité professionnelle</li>
                <li>• Cette information sert au calcul de la base d'imposition</li>
              </ul>
              <p className="text-[#64748B] text-sm">
                Si vous disposez d'un local professionnel distinct, passez directement à la section C du formulaire.
              </p>
            </div>

            {/* A3 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <span className="bg-[#2563EB] text-white text-sm px-2 py-1 rounded">A3</span>
                Origine de l'établissement
              </h3>
              <p className="text-[#475569] leading-relaxed mb-3">
                Indiquez l'origine de votre établissement en cochant la case correspondante :
              </p>
              <ul className="space-y-2 text-[#475569] mb-4">
                <li>• <strong>Création d'établissement :</strong> Nouvelle activité (cas le plus fréquent)</li>
                <li>• <strong>Acquisition :</strong> Rachat d'une entreprise existante</li>
                <li>• <strong>Transfert :</strong> Déménagement d'un établissement existant</li>
                <li>• <strong>Apport, fusion, scission :</strong> Opérations de restructuration</li>
              </ul>
            </div>

            {/* B1 / B2 */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <span className="bg-[#2563EB] text-white text-sm px-2 py-1 rounded">B1/B2</span>
                Renseignements entreprise et établissement
              </h3>
              <p className="text-[#475569] leading-relaxed mb-3">
                Ces sections concernent les informations économiques de votre activité :
              </p>
              <ul className="space-y-2 text-[#475569] mb-4">
                <li>• <strong>Date de création :</strong> La date de début de votre activité</li>
                <li>• <strong>Effectif salarié :</strong> Nombre de salariés (0 pour la plupart des auto-entrepreneurs)</li>
                <li>• <strong>Chiffre d'affaires estimé :</strong> Prévision de CA pour l'année en cours</li>
                <li>• <strong>Régime micro-entrepreneur :</strong> Cochez cette case si vous êtes auto-entrepreneur</li>
                <li>• <strong>Établissement principal :</strong> Cochez si c'est votre seul établissement</li>
              </ul>
            </div>

            {/* D */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <span className="bg-[#2563EB] text-white text-sm px-2 py-1 rounded">D</span>
                Exonérations de CFE
              </h3>
              <p className="text-[#475569] leading-relaxed mb-3">
                Plusieurs exonérations peuvent s'appliquer selon votre situation :
              </p>
              <ul className="space-y-2 text-[#475569] mb-4">
                <li>• <strong>Entreprise nouvelle :</strong> Exonération automatique la première année civile d'activité</li>
                <li>• <strong>Zone France Ruralités :</strong> Exonération si votre établissement est en zone éligible</li>
                <li>• <strong>Autres exonérations :</strong> Certaines activités spécifiques peuvent bénéficier d'exonérations particulières</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section id="erreurs-frequentes" className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              Erreurs fréquentes à éviter
            </h2>

            <p className="text-[#475569] leading-relaxed mb-4">
              Lors du remplissage du formulaire 1447-C-SD, certaines erreurs reviennent fréquemment. Voici comment les éviter :
            </p>

            <div className="space-y-4">
              <Card className="border-red-100 bg-red-50/50">
                <CardContent className="p-4">
                  <p className="font-medium text-red-800 mb-1">Numéro SIRET incomplet</p>
                  <p className="text-red-700 text-sm m-0">Le SIRET comporte 14 chiffres. Ne confondez pas avec le SIREN (9 chiffres).</p>
                </CardContent>
              </Card>

              <Card className="border-red-100 bg-red-50/50">
                <CardContent className="p-4">
                  <p className="font-medium text-red-800 mb-1">Adresse incomplète ou erronée</p>
                  <p className="text-red-700 text-sm m-0">Indiquez l'adresse exacte de l'établissement, pas votre adresse personnelle si vous avez un local distinct.</p>
                </CardContent>
              </Card>

              <Card className="border-red-100 bg-red-50/50">
                <CardContent className="p-4">
                  <p className="font-medium text-red-800 mb-1">Oubli de cocher "micro-entrepreneur"</p>
                  <p className="text-red-700 text-sm m-0">Cette case est importante pour le calcul de votre cotisation et les éventuelles exonérations.</p>
                </CardContent>
              </Card>

              <Card className="border-red-100 bg-red-50/50">
                <CardContent className="p-4">
                  <p className="font-medium text-red-800 mb-1">Date de création erronée</p>
                  <p className="text-red-700 text-sm m-0">Utilisez la date de début d'activité indiquée sur votre avis de situation SIRENE, pas la date d'immatriculation.</p>
                </CardContent>
              </Card>

              <Card className="border-red-100 bg-red-50/50">
                <CardContent className="p-4">
                  <p className="font-medium text-red-800 mb-1">Ne pas demander l'exonération première année</p>
                  <p className="text-red-700 text-sm m-0">Pensez à cocher la case "entreprise nouvelle" si vous créez votre activité, même si l'exonération est automatique.</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 5 */}
          <section id="generer-formulaire" className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
              <Shield className="h-6 w-6 text-[#2563EB]" />
              Générer automatiquement le formulaire 1447-C-SD
            </h2>

            <p className="text-[#475569] leading-relaxed mb-4">
              Remplir le formulaire 1447-C-SD peut être fastidieux, surtout si vous n'êtes pas familier avec le jargon administratif. 
              Pour vous simplifier la tâche, un <strong>outil de génération automatique</strong> vous permet de :
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Remplir le formulaire étape par étape avec des explications claires</span>
              </li>
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Vérifier automatiquement la cohérence des informations (SIRET, code APE...)</span>
              </li>
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Générer un PDF conforme au formulaire officiel 1447-C-SD</span>
              </li>
              <li className="flex items-start gap-3 text-[#475569]">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Télécharger le document prêt à être signé et envoyé</span>
              </li>
            </ul>

            <p className="text-[#64748B] text-sm mb-6">
              Le document généré doit être vérifié par vos soins avant envoi aux services fiscaux. 
              Ce service n'est pas affilié à l'administration fiscale française.
            </p>
          </section>

          {/* FAQ SEO - Rich Snippets */}
          <section id="faq-cfe" className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <HelpCircle className="h-6 w-6 text-[#2563EB]" />
              Questions fréquentes sur la CFE auto-entrepreneur
            </h2>

            <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
              
              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Faut-il remplir la CFE quand on est auto-entrepreneur ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Oui, tous les auto-entrepreneurs doivent remplir une déclaration initiale de CFE (formulaire 1447-C-SD) lors de la création de leur activité. 
                    Cette déclaration est obligatoire même si vous bénéficiez d'une exonération la première année.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Le formulaire 1447-C-SD est-il obligatoire ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Oui, le formulaire 1447-C-SD est obligatoire pour toute création d'établissement ou changement d'exploitant. 
                    Il doit être envoyé au Service des Impôts des Entreprises (SIE) dont dépend votre activité.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Où envoyer la déclaration CFE ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    La déclaration CFE doit être envoyée au Service des Impôts des Entreprises (SIE) dont dépend l'adresse de votre établissement. 
                    Vous pouvez également la déposer via votre espace professionnel sur impots.gouv.fr.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Peut-on remplir la CFE en ligne ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Le formulaire 1447-C-SD peut être rempli en version papier ou via un outil de génération de PDF. 
                    Une fois généré, le document doit être signé et envoyé aux services fiscaux par courrier ou via votre espace en ligne.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Quand faut-il envoyer le formulaire CFE ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    La déclaration initiale de CFE doit être envoyée avant le 31 décembre de l'année de création de votre activité. 
                    Par exemple, si vous créez votre auto-entreprise en mars 2024, vous devez envoyer le formulaire avant le 31 décembre 2024.
                  </p>
                </div>
              </div>

            </div>
          </section>

          {/* CTA Final */}
          <section className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-[#0F172A] mb-3">
              Prêt à remplir votre déclaration CFE ?
            </h2>
            <p className="text-[#475569] mb-6">
              Générez votre formulaire 1447-C-SD en quelques minutes grâce à notre assistant guidé.
            </p>
            <a href="/#formulaire">
              <Button 
                size="lg"
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg w-full sm:w-auto"
              >
                Commencer ma déclaration CFE
                <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
              </Button>
            </a>
          </section>

        </article>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CFELogo size={28} />
                <span className="font-bold text-lg">CFE Auto-Entrepreneur</span>
              </div>
              <p className="text-slate-400 text-sm">
                Service de génération de déclaration CFE simplifié pour les auto-entrepreneurs français.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Liens utiles</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/#formulaire" className="text-slate-400 hover:text-[#2563EB]">Remplir ma déclaration</a></li>
                <li><a href="/#faq" className="text-slate-400 hover:text-[#2563EB]">FAQ</a></li>
                <li><a href="/comment-remplir-cfe-auto-entrepreneur" className="text-slate-400 hover:text-[#2563EB]">Guide CFE auto-entrepreneur</a></li>
                <li><a href="/mentions-legales" className="text-slate-400 hover:text-[#2563EB]">Mentions légales</a></li>
                <li><a href="/cgv" className="text-slate-400 hover:text-[#2563EB]">CGV</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Informations importantes</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Ce service n&apos;est pas affilié à l&apos;administration fiscale française.</li>
                <li>Ce service ne constitue pas un conseil fiscal.</li>
                <li>Le document généré doit être vérifié par l&apos;utilisateur avant envoi.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-slate-400 text-sm mb-2">Une question ? Contactez-nous :</p>
              <a 
                href="mailto:contact@cfe-autoentrepreneur.fr" 
                className="text-sm text-slate-400 hover:text-[#2563EB] transition-colors"
              >
                contact@cfe-autoentrepreneur.fr
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} CFE Auto-Entrepreneur. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
